import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, TrendingUp, Activity, ChevronRight, Trash2, AlertTriangle, Edit2, Check, X, FlaskConical, Heart, Pill, AlertCircle, Mail, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import SocialMediaSection from '../SocialMediaSection';
import AthleteInfoDisplay from '../shared/AthleteInfoDisplay';
import { API } from '../../config/api';
import { Button } from '@/components/ui/button';

export default function AthleteCard({ child, latestMeasurement, medicalStatus, onDelete, onUpdate }) {
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editingDOB, setEditingDOB] = useState(false);
  const [dobValue, setDobValue] = useState(child.date_of_birth?.split('T')[0] || '');
  const [savingDOB, setSavingDOB] = useState(false);
  
  // Email entry state
  const [showEmailEntry, setShowEmailEntry] = useState(false);
  const [emailType, setEmailType] = useState('player'); // 'player' or 'parent'
  const [emailValue, setEmailValue] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  
  // Calculate age
  const dob = new Date(child.date_of_birth);
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }

  const handleDelete = async (e) => {
    e.stopPropagation();
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please log in again to delete athletes');
        setDeleting(false);
        return;
      }
      await axios.delete(`${API}/children/${child.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${child.first_name} ${child.last_name} and all their data has been deleted`);
      setShowDeleteConfirm(false);
      if (onDelete) {
        onDelete(child.id);
      }
    } catch (error) {
      console.error('Delete error:', error.response?.status, error.response?.data);
      const errorDetail = error.response?.data?.detail || 'Failed to delete athlete';
      if (errorDetail.toLowerCase().includes('credentials') || error.response?.status === 401) {
        toast.error('Session expired. Please refresh the page and log in again.');
      } else {
        toast.error(errorDetail);
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveDOB = async (e) => {
    e.stopPropagation();
    if (!dobValue) {
      toast.error('Please enter a valid date');
      return;
    }
    setSavingDOB(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/children/${child.id}`, {
        date_of_birth: dobValue
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Date of birth updated! Grades are being recalculated.');
      setEditingDOB(false);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update date of birth');
    } finally {
      setSavingDOB(false);
    }
  };

  const handleSaveEmail = async (e, andSendInvite = false) => {
    e.stopPropagation();
    if (!emailValue || !emailValue.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setSavingEmail(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Update the child's email field (same field used in profile)
      await axios.put(`${API}/children/${child.id}`, {
        email: emailValue
      }, { headers });
      
      toast.success(`${emailType === 'player' ? 'Player' : 'Parent'} email saved!`);
      
      // If user wants to send invite immediately
      if (andSendInvite) {
        setSendingInvite(true);
        try {
          await axios.post(`${API}/children/${child.id}/send-invitation`, {
            parent_email: emailValue
          }, { headers });
          toast.success('Invitation sent!');
        } catch (inviteError) {
          toast.error(inviteError.response?.data?.detail || 'Email saved but invitation failed');
        } finally {
          setSendingInvite(false);
        }
      }
      
      setShowEmailEntry(false);
      setEmailValue('');
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save email');
    } finally {
      setSavingEmail(false);
    }
  };

  const openEmailEntry = (e, type) => {
    e.stopPropagation();
    setEmailType(type);
    setEmailValue(child.email || '');
    setShowEmailEntry(true);
  };

  return (
    <div
      data-testid="athlete-card"
      onClick={() => navigate(`/child/${child.id}`)}
      className="group bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-950 rounded-2xl p-6 shadow-xl hover:shadow-2xl hover:shadow-[#02b5ff]/10 transition-all duration-300 cursor-pointer border border-zinc-800 hover:border-[#02b5ff]/50 transform hover:-translate-y-1 relative overflow-hidden"
    >
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div 
          className="absolute inset-0 bg-zinc-900/95 z-20 flex flex-col items-center justify-center p-4 rounded-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <AlertTriangle className="h-10 w-10 text-red-500 mb-3" />
          <h4 className="text-white font-bold text-center mb-2">Delete Athlete?</h4>
          <p className="text-zinc-400 text-xs text-center mb-4">
            This will permanently delete {child.first_name} {child.last_name} and ALL their data including measurements, tests, and records.
          </p>
          <div className="flex gap-2 w-full">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={(e) => {
                e.stopPropagation();
                setShowDeleteConfirm(false);
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="flex-1 bg-red-600 hover:bg-red-500"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </div>
      )}

      {/* Gradient overlay on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#02b5ff]/5 to-[#ff025f]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      <div className="relative z-10">
        <div className="flex items-start justify-between mb-4">
          {child.profile_photo_url ? (
            <img
              src={`${API}${child.profile_photo_url}`}
              alt={`${child.first_name} ${child.last_name}`}
              className="w-14 h-14 rounded-full object-cover ring-4 ring-zinc-800 group-hover:ring-[#02b5ff]/30 transition-all"
            />
          ) : (
            <div className="w-14 h-14 bg-gradient-to-br from-[#02b5ff]/30 to-[#ff025f]/30 rounded-full flex items-center justify-center ring-4 ring-zinc-800 group-hover:ring-[#02b5ff]/30 transition-all">
              <User className="h-7 w-7 text-[#02b5ff]" />
            </div>
          )}
          <div className="flex flex-col items-end gap-1">
            <div className="px-3 py-1 bg-zinc-800/80 backdrop-blur rounded-full border border-zinc-700">
              <span className="text-xs font-semibold text-zinc-300 uppercase">{child.gender}</span>
            </div>
            {child.primary_sport && (
              <div className="px-3 py-1 bg-gradient-to-r from-[#02b5ff]/20 to-[#02b5ff]/10 rounded-full border border-[#02b5ff]/30">
                <span className="text-xs font-semibold text-[#02b5ff]">{child.primary_sport}</span>
              </div>
            )}
          </div>
        </div>
        
        <h3 className="text-xl font-bold text-white mb-1 group-hover:text-[#02b5ff] transition-colors" data-testid={`child-name-${child.id}`}>
          {child.first_name} {child.last_name}
        </h3>
        
        <div className="flex items-center gap-3 text-sm text-zinc-400 mb-2" onClick={(e) => e.stopPropagation()}>
          <span>Age: <span className="text-white font-semibold">{age}</span></span>
          <span className="text-zinc-600">•</span>
          {editingDOB ? (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={dobValue}
                onChange={(e) => setDobValue(e.target.value)}
                className="bg-zinc-800 border border-zinc-600 rounded px-2 py-0.5 text-white text-xs focus:border-[#02b5ff] outline-none"
                onClick={(e) => e.stopPropagation()}
              />
              <button
                onClick={handleSaveDOB}
                disabled={savingDOB}
                className="p-1 text-green-500 hover:bg-green-500/20 rounded"
              >
                <Check className="h-3 w-3" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingDOB(false);
                  setDobValue(child.date_of_birth?.split('T')[0] || '');
                }}
                className="p-1 text-red-500 hover:bg-red-500/20 rounded"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ) : (
            <span className="flex items-center gap-1">
              DOB: {new Date(child.date_of_birth).toLocaleDateString()}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingDOB(true);
                }}
                className="p-1 text-zinc-500 hover:text-[#02b5ff] hover:bg-zinc-800 rounded transition-colors"
                title="Edit date of birth"
              >
                <Edit2 className="h-3 w-3" />
              </button>
            </span>
          )}
        </div>
        
        {/* Email Entry Section */}
        {showEmailEntry ? (
          <div className="bg-zinc-800/50 rounded-lg p-3 mb-2" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-2">
              <Mail className="h-4 w-4 text-[#02b5ff]" />
              <span className="text-xs text-white font-medium">
                {emailType === 'player' ? 'Player Email' : 'Parent Email'}
              </span>
            </div>
            <input
              type="email"
              value={emailValue}
              onChange={(e) => setEmailValue(e.target.value)}
              placeholder="Enter email address"
              className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-white text-sm focus:border-[#02b5ff] outline-none mb-2"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="flex gap-2">
              <button
                onClick={(e) => handleSaveEmail(e, false)}
                disabled={savingEmail}
                className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-zinc-700 hover:bg-zinc-600 text-white text-xs rounded transition-colors"
              >
                {savingEmail ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                Save Only
              </button>
              <button
                onClick={(e) => handleSaveEmail(e, true)}
                disabled={savingEmail || sendingInvite}
                className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-[#02b5ff] hover:bg-[#02b5ff]/80 text-white text-xs rounded transition-colors"
              >
                {sendingInvite ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                Save & Invite
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowEmailEntry(false);
                  setEmailValue('');
                }}
                className="px-2 py-1.5 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 mb-2" onClick={(e) => e.stopPropagation()}>
            {child.email ? (
              <span className="text-xs text-zinc-400 flex items-center gap-1">
                <Mail className="h-3 w-3" />
                {child.email}
              </span>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={(e) => openEmailEntry(e, 'player')}
                  className="text-xs text-[#02b5ff] hover:text-[#02b5ff]/80 flex items-center gap-1 px-2 py-1 bg-[#02b5ff]/10 hover:bg-[#02b5ff]/20 rounded transition-colors"
                  title="Add player email"
                >
                  <Mail className="h-3 w-3" />
                  Player Email
                </button>
                <button
                  onClick={(e) => openEmailEntry(e, 'parent')}
                  className="text-xs text-[#ff025f] hover:text-[#ff025f]/80 flex items-center gap-1 px-2 py-1 bg-[#ff025f]/10 hover:bg-[#ff025f]/20 rounded transition-colors"
                  title="Add parent email"
                >
                  <Mail className="h-3 w-3" />
                  Parent Email
                </button>
              </div>
            )}
            {child.email && (
              <button
                onClick={(e) => openEmailEntry(e, 'player')}
                className="p-1 text-zinc-500 hover:text-[#02b5ff] hover:bg-zinc-800 rounded transition-colors"
                title="Edit email"
              >
                <Edit2 className="h-3 w-3" />
              </button>
            )}
          </div>
        )}
        
        {/* Coach, Team, Manager Info - Using shared component */}
        <AthleteInfoDisplay 
          coachName={child.assigned_coach_name}
          teamName={child.current_team_name}
          managerName={child.assigned_team_manager_name}
          className="mb-2"
        />
        
        {/* Last Measurement & Test Dates - Only show one "no data" message */}
        {age < 21 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {/* Last Measurement */}
            <div className={`text-xs flex items-center gap-1 px-2 py-1 rounded-full ${
              !child.last_measurement_date 
                ? 'bg-[#ff025f]/20 text-[#ff025f] border border-[#ff025f]/30' 
                : (new Date() - new Date(child.last_measurement_date)) > (30 * 24 * 60 * 60 * 1000)
                  ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                  : 'bg-green-500/20 text-green-400 border border-green-500/30'
            }`}>
              <TrendingUp className="h-3 w-3" />
              {!child.last_measurement_date 
                ? 'No measurements' 
                : `Measured: ${new Date(child.last_measurement_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
              }
            </div>
            
            {/* Last Test */}
            <div className={`text-xs flex items-center gap-1 px-2 py-1 rounded-full ${
              !child.last_test_date 
                ? 'bg-zinc-700/50 text-zinc-400 border border-zinc-600' 
                : (new Date() - new Date(child.last_test_date)) > (30 * 24 * 60 * 60 * 1000)
                  ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                  : 'bg-[#02b5ff]/20 text-[#02b5ff] border border-[#02b5ff]/30'
            }`}>
              <FlaskConical className="h-3 w-3" />
              {!child.last_test_date 
                ? 'No tests' 
                : `Tested: ${new Date(child.last_test_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
              }
            </div>
          </div>
        )}
        
        {/* Medical Status - Using pre-loaded data (no API call) */}
        <div className="bg-zinc-800/30 rounded-lg p-3 mb-3" data-testid="medical-status-compact">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Heart className={`h-4 w-4 ${
                !medicalStatus?.has_questionnaire ? 'text-[#ff025f]' :
                medicalStatus?.is_expired ? 'text-yellow-500' :
                'text-green-500'
              }`} />
              <span className="text-sm text-white font-medium">Medical</span>
            </div>
            <span className={`text-xs flex items-center gap-1 ${
              !medicalStatus?.has_questionnaire ? 'text-[#ff025f]' :
              medicalStatus?.is_expired ? 'text-yellow-500' :
              medicalStatus?.days_until_expiry < 30 ? 'text-yellow-400' :
              'text-green-400'
            }`}>
              {!medicalStatus?.has_questionnaire ? (
                <><AlertCircle className="h-3 w-3" /> Not completed</>
              ) : medicalStatus?.is_expired ? (
                <><AlertCircle className="h-3 w-3" /> Expired</>
              ) : medicalStatus?.days_until_expiry < 30 ? (
                `${medicalStatus.days_until_expiry}d left`
              ) : (
                <><Check className="h-3 w-3" /> Complete</>
              )}
            </span>
          </div>
          
          {medicalStatus?.has_questionnaire && !medicalStatus?.is_expired && (
            <div className="mt-2 space-y-1">
              {medicalStatus.has_allergies && medicalStatus.allergies_summary?.length > 0 && (
                <div className="flex items-start gap-1">
                  <AlertCircle className="h-3 w-3 text-[#ff025f] mt-0.5 flex-shrink-0" />
                  <span className="text-xs text-zinc-400 truncate">
                    Allergies: {medicalStatus.allergies_summary.join(', ')}
                  </span>
                </div>
              )}
              {medicalStatus.has_conditions && medicalStatus.conditions_summary?.length > 0 && (
                <div className="flex items-start gap-1">
                  <Pill className="h-3 w-3 text-yellow-400 mt-0.5 flex-shrink-0" />
                  <span className="text-xs text-zinc-400 truncate">
                    {medicalStatus.conditions_summary.join(', ')}
                  </span>
                </div>
              )}
              {!medicalStatus.has_allergies && !medicalStatus.has_conditions && (
                <span className="text-xs text-green-400">No allergies or conditions</span>
              )}
            </div>
          )}
        </div>
        
        {/* Social Media Icons */}
        {(child.facebook_handle || child.instagram_handle || child.tiktok_handle) && (
          <div className="mb-3" onClick={(e) => e.stopPropagation()}>
            <SocialMediaSection child={child} readOnly={true} />
          </div>
        )}
        
        {/* BMI Data Display - Only show if there are measurements */}
        {latestMeasurement && (
          <div className="bg-zinc-800/50 backdrop-blur rounded-xl p-3 mb-3 border border-zinc-700/50">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-xs text-zinc-500">Height</div>
                <div className="text-sm font-bold text-white">{latestMeasurement.standing_height} cm</div>
                {latestMeasurement.uk_who_percentiles?.height_percentile && (
                  <div className="text-xs text-[#02b5ff]">{latestMeasurement.uk_who_percentiles.height_percentile.toFixed(0)}%</div>
                )}
              </div>
              <div>
                <div className="text-xs text-zinc-500">Weight</div>
                <div className="text-sm font-bold text-white">{latestMeasurement.weight} kg</div>
                {latestMeasurement.uk_who_percentiles?.weight_percentile && (
                  <div className="text-xs text-[#02b5ff]">{latestMeasurement.uk_who_percentiles.weight_percentile.toFixed(0)}%</div>
                )}
              </div>
              <div>
                <div className="text-xs text-zinc-500">BMI</div>
                <div className="text-sm font-bold text-white">{latestMeasurement.bmi}</div>
                {latestMeasurement.bmi_percentile && (
                  <div className={`text-xs ${
                    latestMeasurement.bmi_percentile < 5 || latestMeasurement.bmi_percentile > 95 
                      ? 'text-[#ff025f] font-bold' 
                      : 'text-[#02b5ff]'
                  }`}>
                    {latestMeasurement.bmi_percentile.toFixed(0)}%
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        <div className="flex items-center justify-between">
          <div className="flex items-center text-[#02b5ff] font-semibold text-sm group-hover:translate-x-1 transition-transform">
            <Activity className="h-4 w-4 mr-2" />
            View Profile
            <ChevronRight className="h-4 w-4 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          
          {/* Delete Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowDeleteConfirm(true);
            }}
            className="p-2 text-zinc-500 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
            title="Delete player and data"
            data-testid="delete-athlete-btn"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
